import {
  BaseMessageSignerWalletAdapter,
  WalletConnectionError,
  WalletDisconnectionError,
  WalletName,
  WalletNotConnectedError,
  WalletNotReadyError,
  WalletReadyState,
  WalletSignMessageError,
  WalletSignTransactionError,
} from '@solana/wallet-adapter-base'
import { PublicKey, Transaction, VersionedTransaction } from '@solana/web3.js'

/**
 * X1WalletAdapter — connect option for the native X1 Wallet.
 *
 * Mirrors the detection cascade the X1 trading-bot dashboard uses
 * (dashboard/src/utils/walletDetection.ts): the X1 Wallet may surface as an
 * injected window global, as a Wallet-Standard wallet, or as a generic
 * window.solana provider that is NOT Backpack/Phantom. We resolve all three so
 * the button works regardless of how the installed extension exposes itself.
 *
 * Verified against the real X1 Wallet extension v0.10.98, which sets
 * window.x1Wallet, window.x1, AND window.solana (all the same provider, marker
 * `isX1Wallet = true`) and also registers via wallet-standard.
 *
 *   1. window.x1Wallet ?? window.x1 ?? window.x1wallet   (injected extension)
 *   2. window.solana when isX1Wallet, or when !isBackpack && !isPhantom (fallback)
 *   3. navigator.wallets — name contains "x1" + standard:connect  (Wallet Standard)
 *
 * signTransaction / signAllTransactions are OPTIONAL on the X1 provider (the
 * bot guards `typeof === 'function'`), so we surface them only when present.
 */

// Minimal shape of an injected X1 provider (superset of what we call).
interface X1Provider {
  publicKey?: { toBytes?(): Uint8Array; toBase58?(): string } | null
  isConnected?: boolean
  connect(): Promise<{ publicKey?: any } | void>
  disconnect?(): Promise<void>
  signTransaction?<T extends Transaction | VersionedTransaction>(tx: T): Promise<T>
  signAllTransactions?<T extends Transaction | VersionedTransaction>(txs: T[]): Promise<T[]>
  signMessage?(message: Uint8Array): Promise<{ signature: Uint8Array } | Uint8Array>
  on?(event: string, handler: (args: any) => void): void
  off?(event: string, handler: (args: any) => void): void
}

/** Locate an injected X1 provider via the bot's exact cascade (tiers 1 + 3). */
function findInjectedX1Provider(): X1Provider | null {
  if (typeof window === 'undefined') return null
  const w = window as any
  // Tier 1: the extension's dedicated globals (v0.10.98 sets x1Wallet + x1).
  const injected = w.x1Wallet || w.x1 || w.x1wallet
  if (injected && typeof injected.connect === 'function') return injected as X1Provider
  // Tier 2: window.solana — X1 also claims it. Accept it when it self-identifies
  // as X1, or when it's a generic provider that is NOT Backpack/Phantom.
  const sol = w.solana
  if (sol && typeof sol.connect === 'function' && (sol.isX1Wallet || sol.isX1 || (!sol.isBackpack && !sol.isPhantom))) {
    return sol as X1Provider
  }
  return null
}

export const X1WalletName = 'X1 Wallet' as WalletName<'X1 Wallet'>

export interface X1WalletAdapterConfig {}

export class X1WalletAdapter extends BaseMessageSignerWalletAdapter {
  name = X1WalletName
  url = 'https://x1.xyz'
  icon = 'data:image/jpeg;base64,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'
  readonly supportedTransactionVersions = null

  private _connecting = false
  private _wallet: X1Provider | null = null
  private _publicKey: PublicKey | null = null
  private _readyState: WalletReadyState =
    typeof window === 'undefined' ? WalletReadyState.Unsupported : WalletReadyState.Loadable

  constructor(_config: X1WalletAdapterConfig = {}) {
    super()
    if (typeof window !== 'undefined') {
      // Installed if an injected provider is present now; otherwise Loadable so
      // it still appears in the list (Wallet-Standard may register late).
      this._readyState = findInjectedX1Provider() ? WalletReadyState.Installed : WalletReadyState.Loadable
      // The extension injects at document_start, but the adapter may be
      // constructed before that global lands (or before wallet-standard
      // registers). Poll briefly and flip to Installed so the connect modal
      // actually connects instead of falling through to the install URL.
      if (this._readyState !== WalletReadyState.Installed) this._scopePollingDetection()
    }
  }

  private _scopePollingDetection() {
    let tries = 0
    const detect = () => {
      if (this._readyState === WalletReadyState.Installed) return true
      if (findInjectedX1Provider() || this.findStandardWallet()) {
        this._readyState = WalletReadyState.Installed
        this.emit('readyStateChange', this._readyState)
        return true
      }
      return false
    }
    if (detect()) return
    const interval = setInterval(() => {
      // ~3s of polling (60 × 50ms) then give up — enough for a slow inject.
      if (detect() || ++tries > 60) clearInterval(interval)
    }, 50)
    // Also react immediately to a wallet-standard registration event.
    try {
      ;(window as any).addEventListener?.('wallet-standard:register-wallet', () => detect())
    } catch { /* ignore */ }
  }

  get publicKey() { return this._publicKey }
  get connecting() { return this._connecting }
  get connected() { return !!this._publicKey }
  get readyState() { return this._readyState }

  /** Tier 2: a Wallet-Standard wallet whose name contains "x1" with standard:connect. */
  private findStandardWallet(): any | null {
    if (typeof window === 'undefined') return null
    try {
      const registered = (window as any).navigator?.wallets?.get?.() || []
      return registered.find(
        (wlt: any) => (wlt?.name || '').toLowerCase().includes('x1') && wlt?.features?.['standard:connect'],
      ) || null
    } catch {
      return null
    }
  }

  private toPublicKey(pk: any): PublicKey {
    if (!pk) throw new WalletConnectionError('No public key returned from X1 Wallet')
    if (typeof pk.toBytes === 'function') return new PublicKey(pk.toBytes())
    if (typeof pk.toBase58 === 'function') return new PublicKey(pk.toBase58())
    return new PublicKey(String(pk))
  }

  async connect(): Promise<void> {
    try {
      if (this.connected || this.connecting) return
      this._connecting = true

      const injected = findInjectedX1Provider()
      if (injected) {
        const resp = await injected.connect()
        const pk = (resp && (resp as any).publicKey) || injected.publicKey
        this._publicKey = this.toPublicKey(pk)
        this._wallet = injected
        // Reflect account/disconnect changes coming from the extension.
        if (typeof injected.on === 'function') {
          injected.on('disconnect', () => this._handleProviderDisconnect())
          injected.on('accountChanged', (next: any) => {
            if (!next) return this._handleProviderDisconnect()
            try { this._publicKey = this.toPublicKey(next.publicKey || next); this.emit('connect', this._publicKey) } catch { /* ignore */ }
          })
        }
        this.emit('connect', this._publicKey)
        return
      }

      // Tier 2 — Wallet Standard.
      const std = this.findStandardWallet()
      if (std) {
        const result = await std.features['standard:connect'].connect()
        const account = result?.accounts?.[0]
        if (!account) throw new WalletConnectionError('No account returned from X1 Wallet (standard)')
        this._publicKey = new PublicKey(account.address)
        this._wallet = { // wrap the standard wallet's sign features into our provider shape
          connect: async () => {},
          signMessage: account.features?.['solana:signMessage']
            ? async (m: Uint8Array) => account.features['solana:signMessage'].signMessage({ message: m })
            : undefined,
        } as any
        this.emit('connect', this._publicKey)
        return
      }

      throw new WalletNotReadyError('X1 Wallet not found. Install the X1 Wallet extension.')
    } catch (error: any) {
      this.emit('error', error)
      throw error
    } finally {
      this._connecting = false
    }
  }

  private _handleProviderDisconnect() {
    this._wallet = null
    this._publicKey = null
    this.emit('disconnect')
  }

  async disconnect(): Promise<void> {
    const wallet = this._wallet
    this._wallet = null
    this._publicKey = null
    if (wallet && typeof wallet.disconnect === 'function') {
      try { await wallet.disconnect() } catch (error: any) {
        this.emit('error', new WalletDisconnectionError(error?.message, error))
      }
    }
    this.emit('disconnect')
  }

  async signTransaction<T extends Transaction | VersionedTransaction>(transaction: T): Promise<T> {
    try {
      const wallet = this._wallet
      if (!wallet || !this._publicKey) throw new WalletNotConnectedError()
      if (typeof wallet.signTransaction !== 'function') {
        throw new WalletSignTransactionError('X1 Wallet does not support signTransaction')
      }
      try {
        return await wallet.signTransaction(transaction)
      } catch (error: any) {
        throw new WalletSignTransactionError(error?.message, error)
      }
    } catch (error: any) {
      this.emit('error', error)
      throw error
    }
  }

  async signAllTransactions<T extends Transaction | VersionedTransaction>(transactions: T[]): Promise<T[]> {
    try {
      const wallet = this._wallet
      if (!wallet || !this._publicKey) throw new WalletNotConnectedError()
      if (typeof wallet.signAllTransactions === 'function') {
        try { return await wallet.signAllTransactions(transactions) } catch (error: any) {
          throw new WalletSignTransactionError(error?.message, error)
        }
      }
      // Fallback: sign sequentially if only signTransaction exists.
      if (typeof wallet.signTransaction === 'function') {
        const signed: T[] = []
        for (const tx of transactions) signed.push(await wallet.signTransaction(tx))
        return signed
      }
      throw new WalletSignTransactionError('X1 Wallet does not support transaction signing')
    } catch (error: any) {
      this.emit('error', error)
      throw error
    }
  }

  async signMessage(message: Uint8Array): Promise<Uint8Array> {
    try {
      const wallet = this._wallet
      if (!wallet || !this._publicKey) throw new WalletNotConnectedError()
      if (typeof wallet.signMessage !== 'function') {
        throw new WalletSignMessageError('X1 Wallet does not support signMessage')
      }
      try {
        const res = await wallet.signMessage(message)
        // Provider may return { signature } or a raw Uint8Array (bot handles both).
        const sig = (res as any)?.signature ?? res
        return sig as Uint8Array
      } catch (error: any) {
        throw new WalletSignMessageError(error?.message, error)
      }
    } catch (error: any) {
      this.emit('error', error)
      throw error
    }
  }
}
